import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Course, CourseRequest, ICourse, ICourseRequest } from '@app/core';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  constructor(private http: HttpClient) {}

  private requestCountSubject = new BehaviorSubject<number>(0);
  requestCount$: Observable<number> = this.requestCountSubject.asObservable();

  getCourse(id: number): Observable<Course> {
    return this.http
      .get<any>(`${environment.apiUrl}courses/${id}/`)
      .pipe(map((res: ICourse) => new Course(res)));
  }

  getCourses(
    filters: HttpParams | { [params: string]: string | string[] } = {},
    isSilentLoader = false
  ) {
    return this.http
      .get<any>(`${environment.apiUrl}courses/`, {
        params: filters,
        headers: { 'silent-loader': isSilentLoader ? 'true' : 'false' },
      })
      .pipe(
        map(res => ({ ...res, results: res.results.map((course: ICourse) => new Course(course)) }))
      );
  }

  isCourseCodeExists(courseCode: string) {
    return this.http.get<any>(`${environment.apiUrl}courses/check_course_code/`, {
      params: { courseCode },
      headers: { 'silent-loader': 'true' },
    });
  }

  searchCourses(searchTerm: string) {
    return this.http
      .get<any>(`${environment.apiUrl}courses/search/`, {
        params: { query: searchTerm },
        headers: { 'silent-loader': 'true' },
      })
      .pipe(map((res: ICourse[]) => res.map(course => new Course(course))));
  }

  joinCourse(courseCode: string) {
    return this.http.post<any>(`${environment.apiUrl}courses/join/`, { courseCode });
  }

  getRequests(
    filters: HttpParams | { [params: string]: string | string[] } = {},
    isSilentLoader = false
  ) {
    return this.http
      .get<any>(`${environment.apiUrl}usercourses/requests/`, {
        params: filters,
        headers: { 'silent-loader': isSilentLoader ? 'true' : 'false' },
      })
      .pipe(
        map(res => ({
          ...res,
          results: res.results.map(
            (courseRequest: ICourseRequest) => new CourseRequest(courseRequest)
          ),
        }))
      );
  }

  getInvitations(
    filters: HttpParams | { [params: string]: string | string[] } = {},
    isSilentLoader = false
  ) {
    return this.http
      .get<any>(`${environment.apiUrl}usercourses/invitations/`, {
        params: filters,
        headers: { 'silent-loader': isSilentLoader ? 'true' : 'false' },
      })
      .pipe(
        map(res => ({
          ...res,
          results: res.results.map(
            (courseRequest: ICourseRequest): CourseRequest => new CourseRequest(courseRequest)
          ),
        }))
      );
  }

  updateRequestCount(count: number) {
    this.requestCountSubject.next(count);
  }
}
